import { useEffect, useState } from 'react';
import type { Agent, ApplyResult, EvolutionInsight, KnowledgeBase, LearningSuggestion } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

const APPLIABLE = ['prompt_update', 'rule_change', 'triage_adjustment', 'kb_merge', 'teach'];
const FILTERS = ['proposed', 'accepted', 'applied', 'rejected', ''];

export function LearningPage() {
  const [suggestions, setSuggestions] = useState<LearningSuggestion[]>([]);
  const [insights, setInsights] = useState<EvolutionInsight[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [computing, setComputing] = useState(false);
  const [lastApply, setLastApply] = useState<ApplyResult | null>(null);
  const [status, setStatus] = useState('proposed');
  const [teachAgentId, setTeachAgentId] = useState('');
  const [teachTitle, setTeachTitle] = useState('');
  const [teachContent, setTeachContent] = useState('');

  function load(next = status) {
    void apiClient.listLearning(next || undefined).then(setSuggestions).catch(() => setSuggestions([]));
  }

  useEffect(() => {
    load();
    void apiClient.listInsights().then(setInsights).catch(() => setInsights([]));
    void apiClient.listAgents().then((page) => setAgents(page.items)).catch(() => setAgents([]));
    void apiClient.listKnowledgeBases().then(setKnowledgeBases).catch(() => setKnowledgeBases([]));
  }, []);

  function detailLabel(suggestion: LearningSuggestion): string {
    const detail = suggestion.detail as Record<string, unknown>;
    if (suggestion.kind === 'kb_merge' && detail.agentId && detail.knowledgeBaseId) {
      const agent = agents.find((row) => row.id === detail.agentId);
      const kb = knowledgeBases.find((row) => row.id === detail.knowledgeBaseId);
      return `${agent?.name ?? detail.agentId} <- ${kb?.name ?? detail.knowledgeBaseId}`;
    }
    if (suggestion.kind === 'teach' && detail.agentId) {
      const agent = agents.find((row) => row.id === detail.agentId);
      const content = (detail.content as string) ?? '';
      const preview = content.length > 80 ? `${content.slice(0, 80)}...` : content;
      return `${agent?.name ?? detail.agentId}: ${preview}`;
    }
    return '';
  }

  return (
    <div>
      <h1 className="mb-1 text-2xl font-semibold">Evolution & Governed Learning</h1>
      <p className="text-sm text-gray-500">Typed improvement proposals from runs, teaching, and evolution insights. Nothing changes agent behavior until a human applies a proposal. Prompt and rule changes are eval-gated.</p>
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wide text-gray-500">Evolution insights</span>
          <Button variant="outline" size="sm" disabled={computing} onClick={() => {
            setComputing(true);
            void apiClient.computeInsights().then((rows) => {
              setInsights(rows);
              load();
            }).finally(() => setComputing(false));
          }}>{computing ? 'Computing...' : 'Recompute'}</Button>
        </div>
        {insights.length > 0 ? (
          <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-2">
            {insights.map((insight) => (
              <div key={insight.id} className="rounded bg-gray-50 p-2 dark:bg-gray-800">
                <div className="text-xs text-gray-500">{insight.metricKey.replace(/_/g, ' ')}</div>
                <div className="text-lg font-semibold">{insight.metricKey.endsWith('_rate') ? `${Math.round(insight.value * 100)}%` : insight.value}</div>
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-xs text-gray-400">No insights yet. Recompute to generate metrics.</p>}
      </div>
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <span className="text-xs uppercase tracking-wide text-gray-500">Teach an agent</span>
        <select className="mt-2 w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" value={teachAgentId} onChange={(event) => setTeachAgentId(event.target.value)}>
          <option value="">Select agent</option>
          {agents.map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
        </select>
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <input className="rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="Title" value={teachTitle} onChange={(event) => setTeachTitle(event.target.value)} />
          <input className="min-w-48 grow rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="What should the agent learn?" value={teachContent} onChange={(event) => setTeachContent(event.target.value)} />
          <Button size="sm" disabled={!teachAgentId || !teachTitle.trim() || !teachContent.trim()} onClick={() => {
            void apiClient.teach({ agentId: teachAgentId, title: teachTitle.trim(), content: teachContent.trim() }).then(() => {
              setTeachTitle('');
              setTeachContent('');
              load();
            });
          }}>Queue</Button>
        </div>
        <p className="mt-1 text-xs text-gray-400">Queued as a pending proposal; an admin applies it to add a memory.</p>
      </div>
      <div className="mt-4 flex gap-2">
        {FILTERS.map((filter) => (
          <Button key={filter || 'all'} size="sm" variant={status === filter ? 'default' : 'ghost'} onClick={() => { setStatus(filter); load(filter); }}>{filter || 'all'}</Button>
        ))}
      </div>
      {lastApply && (
        <div className={`mt-3 rounded-lg border p-3 text-sm ${lastApply.applied ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
          {lastApply.applied ? 'Applied' : 'Not applied'}{lastApply.target ? ` -> ${lastApply.target}` : ''}
          {lastApply.evalGate?.ran ? ` · eval ${lastApply.evalGate.passed ? 'passed' : 'failed'} (${lastApply.evalGate.passRate == null ? '-' : `${Math.round(lastApply.evalGate.passRate * 100)}%`})` : ''}
          {lastApply.reason ? ` · ${lastApply.reason}` : ''}
        </div>
      )}
      {suggestions.length === 0 && <div className="mt-3 rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">No proposals{status ? ` (${status})` : ''} yet.</div>}
      <div className="mt-3 flex flex-col gap-3">
        {suggestions.map((suggestion) => {
          const detail = suggestion.detail as { input?: string; note?: string; expected?: string };
          const label = detailLabel(suggestion);
          return (
            <div key={suggestion.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">{suggestion.kind}</Badge>
                  <Badge variant="outline">{suggestion.source}</Badge>
                  <strong>{suggestion.title}</strong>
                </div>
                <Badge variant={suggestion.status === 'accepted' ? 'success' : suggestion.status === 'applied' ? 'default' : suggestion.status === 'rejected' ? 'secondary' : 'warning'}>{suggestion.status}</Badge>
              </div>
              {detail.input && <div className="mt-2 text-xs"><span className="text-gray-500">input:</span> {detail.input}</div>}
              {detail.note && <div className="text-xs text-gray-500">{detail.note}</div>}
              {detail.expected && <pre className="mt-2 max-h-32 overflow-auto rounded bg-gray-100 p-2 text-xs dark:bg-gray-800">{detail.expected}</pre>}
              {label && <div className="text-xs text-gray-500">{label}</div>}
              <div className="mt-2 flex gap-2">
                {suggestion.status === 'proposed' && (
                  <>
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => void apiClient.decideLearning(suggestion.id, true).then(() => load())}>Accept</Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => void apiClient.decideLearning(suggestion.id, false).then(() => load())}>Reject</Button>
                  </>
                )}
                {APPLIABLE.includes(suggestion.kind) && (suggestion.status === 'accepted' || suggestion.status === 'proposed') && (
                  <Button size="sm" onClick={() => void apiClient.applyLearning(suggestion.id).then((result) => { setLastApply(result); load(); })}>Apply</Button>
                )}
                {suggestion.status !== 'proposed' && suggestion.reason && <span className="self-center text-xs text-gray-500">"{suggestion.reason}"</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
